import { parseLocalDate } from '@/utils/dates';
import { differenceInBusinessDays } from 'date-fns';

export interface BreachTask {
  id: number;
  name: string;
  start_date: string;
  end_date: string;
  project_id: number | null;
}

export interface BreachProject {
  id: number;
  name: string;
  committed_delivery_date: string | null;
}

export interface DateBreach {
  project: BreachProject;
  committedDate: Date;
  currentEstimatedDate: Date;
  newEstimatedDate: Date;
  daysBreach: number;
  affectedTasks: BreachTask[];
}

export function calculateProjectCompletionDate(tasks: BreachTask[]): Date {
  if (tasks.length === 0) return new Date();
  return tasks.reduce<Date>((latest, task) => {
    const end = parseLocalDate(task.end_date);
    return end > latest ? end : latest;
  }, parseLocalDate(tasks[0]!.end_date));
}

/**
 * Detect committed-date breaches caused by moving tasks.
 *
 * For every project touched by `updatedTasks`, computes the new estimated
 * completion date (latest end_date across all tasks after applying updates)
 * and compares it to the project's committed_delivery_date. Returns a breach
 * entry for every project whose new estimated completion exceeds its committed
 * date.
 */
export function detectDateBreaches(
  updatedTasks: BreachTask[],
  projects: BreachProject[],
  allTasks: BreachTask[],
): DateBreach[] {
  const breaches: DateBreach[] = [];

  const projectIds = [...new Set(updatedTasks.map(t => t.project_id).filter((id): id is number => id !== null))];

  for (const projectId of projectIds) {
    const project = projects.find(p => p.id === projectId);
    if (!project || !project.committed_delivery_date) continue;

    const committedDate = parseLocalDate(project.committed_delivery_date);
    if (isNaN(committedDate.getTime())) continue;

    // Merge updated positions into the full project task list
    const originalProjectTasks = allTasks.filter(t => t.project_id === projectId);
    const projectTasks = originalProjectTasks.map(t => {
      const updated = updatedTasks.find(u => u.id === t.id);
      return updated ?? t;
    });

    // Skip projects with no tasks — completion date would be meaningless
    if (projectTasks.length === 0) continue;

    const currentEstimatedDate = calculateProjectCompletionDate(originalProjectTasks);
    const newEstimatedDate = calculateProjectCompletionDate(projectTasks);

    if (newEstimatedDate > committedDate) {
      const daysBreach = Math.max(0, differenceInBusinessDays(newEstimatedDate, committedDate));
      breaches.push({
        project,
        committedDate,
        currentEstimatedDate,
        newEstimatedDate,
        daysBreach,
        affectedTasks: updatedTasks.filter(t => t.project_id === projectId),
      });
    }
  }

  return breaches;
}
